/**
 * Something is covering the page.
 *
 * Every modal — the sheet, the confirm dialog, the palette — says so on its
 * way in and takes it back on its way out, and the shell reads one class off
 * the body to know. On a phone that class slides the tab bar away, because a
 * modal owns the screen: a row of tabs showing through a scrim is five ways
 * to leave a form you have not saved, and on one browser it was covering the
 * save button itself.
 *
 * A stack, not a counter: a confirm dialog opens over a sheet, and closing
 * the dialog must not uncover the tab bar while the sheet is still up. And
 * only the one on top answers Escape — the sheet under a dialog used to
 * catch the press meant for the dialog, ask its question a second time, and
 * the dialog could not be dismissed from the keyboard at all.
 */
export interface Hold {
  (): void;
  /** Whether nothing modal has opened over this one since. */
  isTop(): boolean;
}

const stack: symbol[] = [];

export function holdModal(): Hold {
  const token = Symbol('modal');
  stack.push(token);
  document.body.classList.add('has-modal');
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    const at = stack.indexOf(token);
    if (at >= 0) stack.splice(at, 1);
    if (stack.length === 0) document.body.classList.remove('has-modal');
  };
  return Object.assign(release, { isTop: () => stack[stack.length - 1] === token });
}
